import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Partner, PartnerType, SettlementPeriodicity } from '../models/partner.model';
import { UserType } from '../models/user.model';
import { PartnerFile } from '../models/partner-file.model';
import { ListPartnerConditionLogsResponse } from '../models/partner-condition-log.model';

export interface ListPartnersParams {
  page?: number;
  per_page?: number;
  search?: string;
  type?: PartnerType;
  relationship_type?: UserType;
  status?: Partner['status'];
}

export interface ListPartnerConditionLogsParams {
  page?: number;
  per_page?: number;
}

export interface ListPartnersResponse {
  data: Partner[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  private readonly http = inject(HttpClient);

  getPartners(params?: ListPartnersParams): Observable<ListPartnersResponse> {
    return this.http.get<ListPartnersResponse>(`${environment.apiUrl}/partners`, {
      params: params as any,
    });
  }

  getPartner(id: string): Observable<{
    data: Partner;
  }> {
    return this.http.get<{
      data: Partner;
    }>(`${environment.apiUrl}/partners/${id}`);
  }

  updatePartner(
    id: string,
    data: {
      type: PartnerType;
      comments?: string;
      relationship_type?: UserType;
      fixed_commission: number | null;
      recurring_commission: number;
      operation_limit?: number | null;
      settlement_periodicity?: SettlementPeriodicity;
      min_new_clients_per_year?: number | null;
    }
  ): Observable<{
    success: boolean;
    data: Partner;
  }> {
    return this.http.patch<{
      success: boolean;
      data: Partner;
    }>(`${environment.apiUrl}/partners/${id}`, data);
  }

  getPartnersWithDeals(): Observable<{
    success: string;
    total: number;
    data: { id: number; company_name: string }[];
  }> {
    return this.http.get<{
      success: string;
      total: number;
      data: { id: number; company_name: string }[];
    }>(`${environment.apiUrl}/partners/with-deals`);
  }

  getFiles(partnerId: string): Observable<PartnerFile[]> {
    return this.http.get<PartnerFile[]>(`${environment.apiUrl}/partners/${partnerId}/files`);
  }

  uploadFile(partnerId: string, file: File): Observable<PartnerFile> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<PartnerFile>(`${environment.apiUrl}/partners/${partnerId}/files`, formData);
  }

  downloadFile(file: PartnerFile): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/partner-files/${file.id}/download`, {
      responseType: 'blob'
    });
  }

  deleteFile(fileId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${environment.apiUrl}/partner-files/${fileId}`);
  }

  downloadContract(number: number): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/contracts/${number}/download`, {
      responseType: 'blob',
    });
  }

  signContract(number: number, signatureImage: string): Observable<{ data: Partner }> {
    return this.http.post<{ data: Partner }>(`${environment.apiUrl}/contracts/${number}/sign`, {
      signature_image: signatureImage,
    });
  }

  updateStatus(
    id: string,
    status: Partner['status']
  ): Observable<{ success: boolean; data: Partner }> {
    return this.http.patch<{ success: boolean; data: Partner }>(
      `${environment.apiUrl}/partners/${id}/status`,
      { status }
    );
  }

  getConditionLogs(
    partnerId: string,
    params?: ListPartnerConditionLogsParams
  ): Observable<ListPartnerConditionLogsResponse> {
    return this.http.get<ListPartnerConditionLogsResponse>(
      `${environment.apiUrl}/partners/${partnerId}/condition-logs`,
      { params: params as any }
    );
  }
}
